// A route's editable fields (TX-US-0030, TX-US-0032): what the table shows as inputs, what is
// sent, and how a calculated figure reads. Numbers stay strings in both directions, so none passes
// through a float.

export const DEAL_FIELDS = [
  { key: 'product', label: 'Product' },
  { key: 'deal_details', label: 'Deal details' },
];

export const NAME_FIELD = { key: 'name', label: 'Route' };

export const PLACE_FIELDS = [
  { key: 'loading_point', label: 'Loading point' },
  { key: 'discharge_point', label: 'Discharge point' },
];

// In the order of their columns.
export const TEXT_FIELDS = [...DEAL_FIELDS, NAME_FIELD, ...PLACE_FIELDS];

export const DAY_FIELDS = [
  { key: 'loading_days', label: 'Loading / queue (days)' },
  { key: 'loaded_travel_days', label: 'Loaded travel (days)' },
  { key: 'offloading_days', label: 'Offloading / wait (days)' },
  { key: 'return_travel_days', label: 'Return travel (days)' },
];

// Both in the route's unit, which has its own column after them.
export const VOLUME_FIELDS = [
  { key: 'truck_capacity_m3', label: 'Truck capacity' },
  { key: 'monthly_target_m3', label: 'Monthly target' },
];

export const UNIT_FIELD = { key: 'unit', label: 'Unit' };

// The units a route is measured in, as the API names them and as they read.
export const UNITS = [
  { value: 'M3', label: 'm³' },
  { value: 'MT', label: 'MT' },
  { value: 'L', label: 'L' },
];

export const unitLabel = (unit) => UNITS.find(({ value }) => value === unit).label;

const NUMBER_FIELDS = [...DAY_FIELDS, ...VOLUME_FIELDS];
const FIELDS = [...TEXT_FIELDS, ...NUMBER_FIELDS, UNIT_FIELD];

// What the API accepts for a day or a volume: non-negative, four decimal places at most.
const NUMBER = /^\d{1,9}(\.\d{1,4})?$/;

export const draftOf = (route) => Object.fromEntries(FIELDS.map(({ key }) => [key, route[key]]));

// The draft as it is sent: trimmed, and a number left empty is zero.
export const fieldsOf = (draft) => ({
  ...Object.fromEntries(TEXT_FIELDS.map(({ key }) => [key, draft[key].trim()])),
  ...Object.fromEntries(NUMBER_FIELDS.map(({ key }) => [key, draft[key].trim() || '0'])),
  unit: draft.unit,
});

export const changed = (route, draft) => {
  const sent = fieldsOf(draft);
  return FIELDS.some(({ key }) => sent[key] !== route[key]);
};

// The keys of the fields the API would refuse.
export const invalidFields = (draft) => {
  const sent = fieldsOf(draft);
  return [
    ...(sent.name ? [] : ['name']),
    ...NUMBER_FIELDS.filter(({ key }) => !NUMBER.test(sent[key])).map(({ key }) => key),
  ];
};

// Display only. A figure whose inputs are not there yet is a dash, as the workbook's blank cell.
export const figure = (value, places = 0) => (
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat('en-US', { maximumFractionDigits: places }).format(Number(value))
);

// The monthly target of the routes together: one figure a unit, since two units do not add up.
export const targetTotals = (targets) => (
  targets.map(({ unit, monthly_target: target }) => `${figure(target, 2)} ${unitLabel(unit)}`).join(' · ')
);

export const nextRouteName = (lines) => `Route ${lines.length + 1}`;
